import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CreateRelationChangeRequestDocument,
  type CreateRelationChangeRequestMutation,
  type CreateRelationChangeRequestMutationVariables,
} from "~/features/relations/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface ActivationTarget {
  id: string;
  recordMetadataId: string;
}

/**
 * Activate / deactivate a relation. In Cnut this is not a direct mutation — it
 * creates a Change Request that flips `deactivatedAt`. The hook returns plain
 * submitters + a `processing` flag; the calling container owns the confirm dialog.
 */
export function useRelationActivation() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [createChangeRequest] = useMutation<
    CreateRelationChangeRequestMutation,
    CreateRelationChangeRequestMutationVariables
  >(CreateRelationChangeRequestDocument);

  const submit = useCallback(
    async (target: ActivationTarget, deactivate: boolean) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      try {
        await createChangeRequest({
          variables: {
            input: {
              tenantId,
              relationId: target.id,
              recordMetadataId: target.recordMetadataId,
              modelPropsInput: { deactivatedAt: deactivate ? new Date().toISOString() : null },
              description: "",
              context: { action: deactivate ? "RelationDeactivateButton" : "RelationActivateButton" },
            },
          },
        });
        toast.success(
          deactivate
            ? t("feature.entities.detail.requestedDeactivateSuccessfully")
            : t("feature.entities.detail.requestedActivateSuccessfully"),
        );
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [createChangeRequest, tenantId, t],
  );

  const onActivate = useCallback((target: ActivationTarget) => submit(target, false), [submit]);
  const onDeactivate = useCallback((target: ActivationTarget) => submit(target, true), [submit]);

  return { onActivate, onDeactivate, processing };
}
